import { IndustryConfig } from './industry-config.interface';
import { IndustryType } from './industry-types.enum';
import { MobileIndustryConfig } from './mobile-industry.config';
import { PharmacyIndustryConfig } from './pharmacy-industry.config';
import { RestaurantIndustryConfig } from './restaurant-industry.config';
import { RetailIndustryConfig } from './retail-industry.config';

/**
 * Industry Configuration Factory
 * Central point for retrieving industry-specific configurations
 */
export class IndustryConfigFactory {
    /**
     * Get configuration for a specific industry
     * @param industryType - The industry type code
     * @returns IndustryConfig for the specified industry
     */
    static getConfig(industryType: number | string): IndustryConfig {
        const id = industryType != null ? Number(industryType) : null;
        switch (id) {
            case IndustryType.RETAIL:
                return RetailIndustryConfig;

            case IndustryType.MOBILE:
                return MobileIndustryConfig;

            case IndustryType.RESTAURANT:
                return RestaurantIndustryConfig;

            case IndustryType.PHARMACY:
                return PharmacyIndustryConfig;

            default:
                console.warn(`Unknown industry type: ${industryType}, defaulting to Retail`);
                return RetailIndustryConfig;
        }
    }

    /**
     * Check if an industry requires API path modification
     * @param industryType - The industry type code
     * @returns true if API paths should be modified
     */
    static requiresApiModification(industryType: number): boolean {
        const config = this.getConfig(industryType);
        return config.apiPathSegment !== null;
    }

    /**
     * Get API path segment for an industry
     * @param industryType - The industry type code
     * @returns The API path segment or null
     */
    static getApiPathSegment(industryType: number): string | null {
        const config = this.getConfig(industryType);
        return config.apiPathSegment;
    }

    /**
     * Get all supported industry types
     * @returns Array of supported industry type codes
     */
    static getSupportedIndustries(): number[] {
        return [IndustryType.RETAIL, IndustryType.MOBILE, IndustryType.RESTAURANT, IndustryType.PHARMACY];
    }
}
